import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../app/store";
import { removeCTe, addCTe, updateCTe, clearCTe, updateNFe } from "../../../../features/manifestoSlice"
import { formatMoeda, getNumeroNota, getUuidV4, handleNumberOnly, isNumber } from "../../../../utils/utils";
import { Button, Card, Col, Dropdown, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBox, faBuilding, faEllipsis, faGear, faPlus, faSave, faXmark } from "@fortawesome/free-solid-svg-icons";
import { ButtonEditar, ButtonRemover, ButtonTrash, JTable, JvCaption, SpanIcon } from "../../../../components/Components";
import CTesProdutoPerigoso from "./CTesProdutoPerigoso";
import { infCTesInterface, infMunDescargaInterface, SelectObjectRemoveInterface } from "../../../../interfaces";
import CTesUnidadeTransp from "./CTesUnidadeTransp";
import { useQuestion } from "../../../../components/ModalQuestion/useModalQuestion";
import { useModalMessage } from "../../../../components/ModalMessage/useModalMessage";
import { Link } from "react-router-dom";

const initialForm: infCTesInterface = {
    chCTe: '',
    SegCodBarra: '',
    municipio_id: 0,
    name: '',
    indReentrega: false,
    peri: [],
    id: 0,
    infUnidTransp: [],
}
const LIMITE: number = 10000;

interface ValueDelete extends SelectObjectRemoveInterface {
    idCTe: number | string;
}


const CTes: React.FC = () => {
    const dispatch = useDispatch();
    const munDescarga = useSelector((state: RootState) => state.manifesto.manifesto.infMunDescarga);
    const selectMunicipiosDescarregamento = useSelector((state: RootState) =>
        state.manifesto.selectMunicipiosDescarregamentos);
    const manifesto = useSelector((state: RootState) => state.manifesto.manifesto);
    const munCTeSelectRef = useRef<HTMLSelectElement>(null);
    const [notaConhecimento, setNotaConhecimento] = useState<infCTesInterface>(initialForm);
    const [showProdutoPerigoso, setShowProdutoPerigoso] = useState<boolean>(false);
    const [change, setChange] = useState<boolean>(false);
    const { confirm, ConfirmModal } = useQuestion();
    const { alertModal, ModalMessageEl } = useModalMessage();
    const [saldo, setSaldo] = useState<number>(0);
    const [selecionado, setSelecionado] = useState<number | string>('');
    const [idCTe, setIdCTe] = useState<number | string>(0)
    const [showUnidadeTransporte, setShowUnidadeTransporte] = useState<boolean>(false);

    const handleCloseUnidadeTransp = () => {
        setShowUnidadeTransporte(false);
        setSelecionado('');
    }

    const handleShowUnidadeTransporte = (id: number | string) => {
        if (change) {
            alertModal('Existem alterações não salvas.');
        } else {
            setIdCTe(id);
            setShowUnidadeTransporte(true);
            setSelecionado(id);
        }
    }


    const handleUpdate = () => {
        if (validaPreenchimento()) {
            dispatch(updateCTe({
                ...notaConhecimento,
                name: munCTeSelectRef.current?.options[munCTeSelectRef.current?.selectedIndex].text || ''
            }));
            setNotaConhecimento(initialForm);
            setChange(false);
            setShowProdutoPerigoso(false);
            setSelecionado('');
        }
    }


    const validaPreenchimento = (): boolean => {

        if (notaConhecimento.chCTe === "") {
            alertModal('Informe uma chave CTe.');
            return false;
        }

        if (!isNumber(notaConhecimento.chCTe)) {
            alertModal('Chave CT-e aceita somente números.');
            return false;
        }

        if (notaConhecimento.SegCodBarra?.trim() !== "") {
            if (!isNumber(notaConhecimento.SegCodBarra)) {
                alertModal('Seg. Cod. Barras aceita somente números.');
                return false;
            }
        }

        if (notaConhecimento.chCTe.substring(20, 22) !== '57') {
            alertModal('Chave informada não é uma CT-e.');
            return false;
        }

        if (notaConhecimento.chCTe.length !== 44) {
            alertModal('Chave deve conter 44 caracteres.');
            return false;
        }

        if (munDescarga.flatMap((item: infMunDescargaInterface) => item.infCTe).find((item: infCTesInterface) =>
            item.chCTe === notaConhecimento.chCTe && item.id !== notaConhecimento.id)) {
            alertModal('Chave já foi adicionada.');
            return false;
        }

        return true;
    }

    const handleRemover = async (value: ValueDelete) => {
        if (change) {
            alertModal("Existem alterações não salvas.");
            return;
        }

        setSelecionado(value.idCTe);
        const confirmed = await confirm({
            message: <>Remover chave: <strong>{getNumeroNota(value.title)}</strong> ?</>,
            title: "Confirmação"
        });
        
        if (confirmed) {
            dispatch(removeCTe({ municipio_id: parseInt(value.id.toString()), id: value.idCTe.toString() }));
        }
        setSelecionado('')
    }

    const handleRemoverTudo = async () => {
        if (change) {
            alertModal("Existem alterações não salvas.");
            return;
        }

        if (munDescarga.flatMap(item => item.infCTe).length > 0) {
            const confirmed = await confirm({
                message: <>Remover todas as chaves CT-e ?</>,
                title: "Confirmação"
            });
            if (confirmed) {
                dispatch(clearCTe());
            }
        }
    }

    const handleAdd = () => {
        if (munCTeSelectRef.current && munCTeSelectRef.current?.value !== "") {
            const nomeMunicipio = munCTeSelectRef.current?.options[munCTeSelectRef.current?.selectedIndex].text || '';

            if (validaPreenchimento()) {

                dispatch(addCTe({
                    ...notaConhecimento,
                    id: getUuidV4(),
                    municipio_id: parseInt(munCTeSelectRef.current?.value),
                    name: nomeMunicipio,
                }));

                setNotaConhecimento(initialForm);
                setShowProdutoPerigoso(false);
            }

        } else {
            alertModal('Selecione um município.');
            return;
        }
    }

    const loadCTe = (id: number | string, municipio_id: number, chCTe: string) => {
        if (change) {
            alertModal('Existem alterações não salvas.');
            return;
        } else {
            const municipio = manifesto.infMunDescarga.find((item: any) => item.municipio_id === municipio_id);
            const nfe = municipio?.infCTe?.find((item: infCTesInterface) => item.chCTe === chCTe);
            setNotaConhecimento({ ...notaConhecimento, ...nfe, municipio_id: municipio_id, name: municipio?.name });
            setChange(true);

            setSelecionado(id);
        }
    }

    const handleCancelarEdicao = () => {
        setChange(false);
        setSelecionado('');
        setNotaConhecimento(initialForm);
    }

    const handleCloseProdutoPerigoso = () => {
        setShowProdutoPerigoso(false);
        setSelecionado('');
    }

    const handleShowProdutoPerigoso = (id: number | string) => {
        if (change) {
            alertModal('Existem alterações não salvas.');
        } else {
            setIdCTe(id);
            setShowProdutoPerigoso(true);
            setSelecionado(id);

        }
    }

    useEffect(() => {
        if (munDescarga?.length > 0) {
            setNotaConhecimento((prev) => (
                {
                    ...prev,
                    municipio_id: munDescarga[0].municipio_id
                }
            ))
        }
    }, [munDescarga]);

    const handleCTeChange = (e: any) => {
        const { name, value } = e.target;
        setNotaConhecimento((prev) => ({ ...prev, [name]: value }))
    }
    useEffect(() => {
        const total = (munDescarga ?? []).reduce((acc: number, item: infMunDescargaInterface) => {
            return acc + (item.infNFe?.length ?? 0);
        }, 0);
        setSaldo(total);

    }, [munDescarga])

    return (<>
        <Card className="mb-2">
            <Card.Header className="fw-bold">
                Formulário de inclusão do CT-e
            </Card.Header>

            <Card.Body>
                <Row className="g-2 mb-3">
                    <Col md={4}>
                        <Form.Group controlId="cte-municipio_id">
                            <Form.Label>*Municipios</Form.Label>
                            <Form.Select name="cte_municipio_id"
                                value={notaConhecimento.municipio_id}
                                onChange={handleCTeChange} ref={munCTeSelectRef}>
                                {selectMunicipiosDescarregamento?.map((item: any) => {
                                    return (<option value={item.id}>{item.name}</option>);
                                })}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md>
                        <Form.Group controlId="cte-chCTe">
                            <Form.Label>*Chave CT-e</Form.Label>
                            <Form.Control name="chCTe" value={notaConhecimento.chCTe}
                                maxLength={44}
                                onKeyUp={handleNumberOnly}
                                type="text" onChange={handleCTeChange} />
                        </Form.Group>
                    </Col>
                    <Col md={'auto'}>
                        <Form.Group controlId="cte-SegCodBarra">
                            <Form.Label>Seg. Cod. Barras</Form.Label>
                            <Form.Control name="SegCodBarra"
                                value={notaConhecimento.SegCodBarra}
                                maxLength={30}
                                onKeyUp={handleNumberOnly} type="text"
                                onChange={handleCTeChange} />
                        </Form.Group>
                    </Col>

                </Row>
                <Row className="g-2">

                    <Col md={'auto'} className="d-flex align-items-end ">
                        <Form.Group>
                            <Form.Switch className="mb-0" type="switch"
                                name="active" label="ind. Reentrega?"
                                onChange={(e) =>
                                    setNotaConhecimento({ ...notaConhecimento, indReentrega: e.target.checked })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Card.Body>
            <Card.Footer>
                <Button className="active me-2" variant="primary"
                    disabled={change}
                    onClick={handleAdd}>
                    <FontAwesomeIcon icon={faPlus} /> Adicionar
                </Button>
                {change && (<>
                    <Button className="active me-2" variant="success"
                        disabled={!change}
                        onClick={handleUpdate}>
                        <FontAwesomeIcon icon={faSave} /> Salvar
                    </Button>

                    <Button className="active me-2" variant="danger"
                        disabled={!change}
                        onClick={handleCancelarEdicao}>
                        <FontAwesomeIcon icon={faXmark} /> Cancalar
                    </Button>

                </>)}

            </Card.Footer>
        </Card>

        <JTable>
            <JvCaption>({saldo} de {formatMoeda(LIMITE, 0, 'decimal')} no máximo)</JvCaption>
            <thead >
                <tr>
                    <th className="text-center" style={{ width: '4rem' }}>
                        <ButtonTrash onClick={() => handleRemoverTudo()} />
                    </th>
                    <th>Municipio</th>
                    <th>Chave CT-e</th>
                    <th>Seg. Cod. Barras</th>
                    <th className="text-center" style={{ width: '2.5rem' }}>
                        <FontAwesomeIcon className="text-primary" icon={faGear} />
                    </th>
                </tr>
            </thead>
            <tbody>
                {munDescarga?.map((item: infMunDescargaInterface) => (
                    <>
                        {item.infCTe?.map((cte: infCTesInterface) => {
                            return (
                                <tr key={cte?.id} className={(cte.id?.toString()) === selecionado.toString() ? 'table-danger' : ''}>
                                    <td className="d-flex justify-content-around ">
                                        <ButtonRemover onClick={() => handleRemover({ id: item.municipio_id, title: cte?.chCTe, idCTe: cte.id?.toString() ?? '' })} />
                                        <ButtonEditar onClick={() => loadCTe(cte.id?.toString() ?? '', item.municipio_id, cte?.chCTe)} />
                                    </td>
                                    <td>{item?.name}</td>
                                    <td>{cte?.chCTe}</td>
                                    <td>{cte?.SegCodBarra}</td>
                                    <td className="text-center p-0 m-0">
                                        <Dropdown className="d-grid gap-2"  >
                                            <Dropdown.Toggle id="dropdown-basic" size="sm" title="Opções"
                                                variant=""
                                                className="no-caret rounded-1 btn btn-sm">
                                                <FontAwesomeIcon icon={faEllipsis} />
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                                <Dropdown.Item as={Link} to={``}
                                                    onClick={() => handleShowProdutoPerigoso(cte?.id?.toString() ?? 0)}>
                                                    <FontAwesomeIcon style={{ minWidth: '1.25rem' }} icon={faBuilding}
                                                        className="text-primary me-1" />
                                                    <SpanIcon count={cte?.peri?.length ?? 0} />
                                                    Produtos Perigosos ONU
                                                </Dropdown.Item>
                                                <Dropdown.Item as={Link} to={``}
                                                    onClick={() => handleShowUnidadeTransporte(cte?.id?.toString() ?? 0)}>
                                                    <FontAwesomeIcon style={{ minWidth: '1.25rem' }} icon={faBox}
                                                        className="text-info me-1" />
                                                    <SpanIcon count={cte?.infUnidTransp?.length ?? 0} />
                                                    Unidades de Transporte
                                                </Dropdown.Item>

                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            )
                        })}
                    </>
                ))}
            </tbody>
        </JTable>
        <CTesProdutoPerigoso
            show={showProdutoPerigoso}
            handleClose={handleCloseProdutoPerigoso}
            id={idCTe} />

        <CTesUnidadeTransp
            show={showUnidadeTransporte}
            handleClose={handleCloseUnidadeTransp}
            id={idCTe} />

        {ConfirmModal}
        {ModalMessageEl}
    </>)
}

export default CTes;
